import React, { useRef, useState, useEffect } from "react";
import useClickOutside from "../../../hook/UseClickOutSide";
import { Tooltip } from "@mantine/core";
import axios from "axios";
import { GetLocationSearch } from "../../../api/map";
var turf = require("@turf/turf");

export interface LocationResponseType {
  numberMatched: number;
  numberReturned: number;
  items: Item[];
}

export interface Item {
  placeName: string;
  subDistrict?: string;
  district?: string;
  city: string;
  postalCode: string;
  bbox: number[];
}

export interface MapsSearchProps {
  onSelectLocation: (value: number[]) => void;
}

function MapsSearch({ onSelectLocation }: MapsSearchProps) {
  const [isSearch, setIsSearch] = useState(false);
  const serchRef = useRef(null);
  const [places, setPlaces] = useState<Item[]>([]);
  const [keywords, setKeywords] = useState<string>("");
  const [coordinates, setCoordinates] = useState<number[]>([]);

  const fetchLocation = async (keyword = "ขอนแก่น") => {
    try {
      const data = (await GetLocationSearch(keyword)) as LocationResponseType;
      if (data.items.length > 0) {
        setPlaces(data.items);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const handleShowInput = () => {
    setIsSearch(true);
  };
  const handleFilterLocation = (value: string) => {
    setKeywords(value);
  };
  const handleClickLocation = (location: Item) => {
    if (!location) return;
    const bbox = turf.bboxPolygon(location?.bbox);
    const center = turf.center(bbox);
    if (center) {
      onSelectLocation(center.geometry.coordinates);
    }
  };
  const handleGetCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setCoordinates([longitude, latitude]);
      },
      (error) => {
        console.log(error);
      }
    );
  };
  const handleCalculateDistance = (location: Item) => {
    if (!location) return 0;
    if (coordinates.length < 1) return 0;

    const bbox = turf.bboxPolygon(location?.bbox);
    const center = turf.center(bbox);
    const point = turf.point([Number(coordinates[0]), Number(coordinates[1])]);
    var options = { units: "kilometers" };
    const distance: number = turf.distance(point, center, options);
    return distance.toFixed(2);
  };
  const tooltipStyles = {
    tooltip: {
      backgroundColor: "white",
      color: "#323232",
      fontSize: "12px",
    },
  };

  useClickOutside(serchRef, () => {
    setIsSearch(false);
  });

  useEffect(() => {
    if (keywords.trim() !== "") {
      fetchLocation(keywords);
    }
  }, [keywords]);

  useEffect(() => {
    handleGetCurrentLocation();
  }, []);

  useEffect(() => {
    fetchLocation();
  }, [coordinates]);

  return (
    <div
      className={`z-10  ${
        isSearch &&
        "bg-primary-contrast shadow-z8 w-full  h-full p-2 rounded-2xl "
      }`}
      ref={serchRef}
    >
      <Tooltip
        label="ค้นหา"
        position="left"
        styles={tooltipStyles}
        disabled={isSearch}
      >
        <div
          className="bg-primary-contrast duration-300 w-8  h-8 p-2 rounded-[10px] border-[1px] border-gray-1  flex gap-2 cursor-pointer"
          onClick={handleShowInput}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 6.47137C0 10.0395 2.93143 12.9427 6.5343 12.9427C7.95907 12.9427 9.26101 12.4886 10.3337 11.7263L14.3623 15.7243C14.5507 15.9108 14.7963 16 15.0583 16C15.6152 16 16 15.5864 16 15.0431C16 14.7836 15.9018 14.5484 15.7298 14.3781L11.7257 10.3883C12.5691 9.30158 13.0686 7.94728 13.0686 6.47137C13.0686 2.9032 10.1372 0 6.5343 0C2.93143 0 0 2.9032 0 6.47137ZM1.40021 6.47137C1.40021 3.66547 3.70112 1.38672 6.5343 1.38672C9.36745 1.38672 11.6684 3.66547 11.6684 6.47137C11.6684 9.27724 9.36745 11.556 6.5343 11.556C3.70112 11.556 1.40021 9.27724 1.40021 6.47137Z"
              fill="#8E8E8E"
            />
          </svg>

          {isSearch && (
            <input
              placeholder="ค้นหา..."
              value={keywords}
              className="focus:!outline-none w-[220px] placeholder:text-text-secondary text-sm font-normal"
              onChange={(e) => handleFilterLocation(e.target.value)}
            />
          )}
        </div>
      </Tooltip>

      {isSearch && (
        <div className="z-10 flex flex-col gap-3 my-4 max-h-[200px] w-full  overflow-y-auto">
          {places.length > 0 ? (
            <>
              {places?.map((e, index) => (
                <button
                  key={index}
                  className={"flex gap-3"}
                  onClick={() => handleClickLocation(e)}
                >
                  <div className="flex flex-col gap-1 items-center w-fit">
                    <div className=" p-1 w-6 h-6 rounded-full bg-primary-light">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 22 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M11 13.5918C11.8484 13.5918 12.6621 13.2548 13.2622 12.6551C13.8622 12.0553 14.1996 11.2418 14.2 10.3934C14.2 9.54467 13.8628 8.73073 13.2627 8.13062C12.6626 7.5305 11.8487 7.19336 11 7.19336C10.1513 7.19336 9.33736 7.5305 8.73725 8.13062C8.13713 8.73073 7.79999 9.54467 7.79999 10.3934C7.80041 11.2418 8.13774 12.0553 8.73781 12.6551C9.33788 13.2548 10.1516 13.5918 11 13.5918Z"
                          stroke="#C34A25"
                          strokeLinecap="square"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M20.6 10.3934C20.6 18.3886 12.6 23.1854 11 23.1854C9.39999 23.1854 1.39999 18.3886 1.39999 10.3934C1.40127 7.84831 2.41331 5.40791 4.21356 3.60885C6.01382 1.80979 8.45489 0.79938 11 0.799805C16.3008 0.799805 20.6 5.0958 20.6 10.3934Z"
                          stroke="#C34A25"
                          strokeLinecap="square"
                        />
                      </svg>
                    </div>
                    <p className="text-xs font-normal  text-gray-6 line-clamp-1 ">
                      {handleCalculateDistance(e)} กม.
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 text-left">
                    <p className="text-sm font-normal text-text-primary ">
                      {e.placeName}
                    </p>
                    <p className="text-xs font-normal text-gray-6 line-clamp-1 ">
                      {`${e.district ? `อ.${e.district}` : ""} ${
                        e.city ? `จ.${e.city}` : ""
                      }`}
                    </p>
                  </div>
                </button>
              ))}
            </>
          ) : (
            <p className="text-sm font-normalself-center text-center py-2 text-text-disabled">
              ไม่พบสถานที่ที่คุณต้องการ
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default MapsSearch;
